import type {
  ConversationClusteringRunLensType,
  ConversationClusteringRunOutputV1,
} from "@/types_api/backend/transport/get_latest_conversation_clustering_run_output_response"
import type {
  ConversationClusteringFailureLensOutputV2,
  ConversationClusteringRunOutputV2,
} from "@/types_api/backend/transport/get_latest_failure_pattern_run_output_response"
import type { AgentRecord } from "@/types/api/agent"

import { RUN_LENS_DISPLAY_ORDER } from "../utils/conversationClusterDescriptions"
import { resolveFailureTableIds } from "./uploadedConversationsFailureTableIds"
import {
  resolveFakeLensConversationAssignments,
  resolveFirstSentence,
} from "./uploadedConversationsVisualizerFakeConversations"
import {
  PLATE_DOMAIN_RADIUS,
  type ChladniPreset,
} from "./uploadedConversationsVisualizerParticles"
import {
  PLACEHOLDER_AGENTS,
  resolvePlaceholderLens,
  type PlaceholderBucket,
  type PlaceholderLens,
} from "./uploadedConversationsVisualizerPlaceholderLenses"

export interface VisualizerConversation {
  dotId?: string
  firstSentence: string
  id: string
  isSelectable?: boolean
  position?: VisualizerClusterSeedPosition
}

interface VisualizerClusterSeedPosition {
  x: number
  y: number
}

export interface VisualizerBucketAssignment {
  bucketKey: string
  conversations: VisualizerConversation[]
  label: string
  seedPosition?: VisualizerClusterSeedPosition
}

export interface VisualizerCallTopicParent {
  children: PlaceholderBucket[]
  conversationIds: string[]
  count: number
  description: string
  parentId: string
  title: string
}

export interface VisualizerLens {
  bucketAssignments: VisualizerBucketAssignment[]
  buckets: PlaceholderBucket[]
  callTopicParents?: VisualizerCallTopicParent[]
  key: string
  failureOutput?: ConversationClusteringFailureLensOutputV2
  preset: ChladniPreset
  subtitle: string
  title: string
}

interface VisualizerAgent {
  key: string
  label: string
  lenses: VisualizerLens[]
}

export const NO_LENS_VISUALIZER_LENS_KEY = "no-lens"
const ALL_CONVERSATIONS_BUCKET_KEY = "all-conversations"
const NO_LENS_DISPLAY_LABEL = "select a lens to cluster calls."
const DEFAULT_FALLBACK_PRESET: ChladniPreset = { m: 3, n: 4 }
const NO_LENS_PRESET_AXIS_MIN = 2
const NO_LENS_PRESET_AXIS_SPAN = 10

function hashVisualizerAgentKey(agentKey: string) {
  let hash = 2166136261
  for (const character of agentKey) {
    hash ^= character.charCodeAt(0)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function normalizeNoLensPresetAxis(value: number) {
  const offset =
    ((value - NO_LENS_PRESET_AXIS_MIN) % NO_LENS_PRESET_AXIS_SPAN) +
    NO_LENS_PRESET_AXIS_SPAN
  return NO_LENS_PRESET_AXIS_MIN + (offset % NO_LENS_PRESET_AXIS_SPAN)
}

function resolveNoLensPreset(agentKey: string, basePreset: ChladniPreset) {
  const hash = hashVisualizerAgentKey(agentKey)
  const hashedM = normalizeNoLensPresetAxis(basePreset.m + (hash & 0xf))
  const hashedN = normalizeNoLensPresetAxis(basePreset.n + ((hash >>> 8) & 0xf))

  if (Math.abs(hashedM - hashedN) >= 2) {
    return { m: hashedM, n: hashedN }
  }

  const shiftedN = normalizeNoLensPresetAxis(hashedN + 5)
  return {
    m: hashedM,
    n:
      shiftedN === hashedM ? normalizeNoLensPresetAxis(shiftedN + 2) : shiftedN,
  }
}

// Run lens types borrow the matching placeholder lens's title, subtitle, and
// Chladni preset; a new backend lens type fails to compile until it is mapped.
const RUN_LENS_PLACEHOLDER_KEYS: Record<
  ConversationClusteringRunLensType,
  PlaceholderLens["key"]
> = {
  anomalies: "anomalies",
  call_topic: "call-topic",
  personas: "personas",
}

type RunLens =
  ConversationClusteringRunOutputV1["agents"][number]["lenses"][number]

function resolveCallTopicParents(
  lens: RunLens,
  buckets: PlaceholderBucket[],
  assignments: VisualizerBucketAssignment[],
) {
  if (lens.lens_type !== "call_topic") {
    return undefined
  }
  const hierarchy = lens.call_topic_hierarchy
  if (!hierarchy) {
    return undefined
  }

  const bucketsById = new Map(buckets.map((bucket) => [bucket.key, bucket]))
  const referencedChildIds = new Set(
    hierarchy.parents.flatMap((parent) => parent.child_cluster_ids),
  )
  if (
    hierarchy.parents.length === 0 ||
    hierarchy.parents.some(
      (parent) =>
        parent.child_cluster_ids.length === 0 ||
        parent.child_cluster_ids.some((childId) => !bucketsById.has(childId)),
    ) ||
    buckets.some((bucket) => !referencedChildIds.has(bucket.key))
  ) {
    return undefined
  }
  const assignmentsById = new Map(
    assignments.map((assignment) => [assignment.bucketKey, assignment]),
  )
  return hierarchy.parents.map((parent): VisualizerCallTopicParent => {
    const children = parent.child_cluster_ids.flatMap((childId) => {
      const bucket = bucketsById.get(childId)
      return bucket ? [bucket] : []
    })
    const conversationIds = [
      ...new Set(
        parent.child_cluster_ids.flatMap((childId) =>
          (assignmentsById.get(childId)?.conversations ?? []).map(
            (conversation) => conversation.id,
          ),
        ),
      ),
    ]
    return {
      children,
      conversationIds,
      count: conversationIds.length,
      description: parent.description,
      parentId: parent.parent_id,
      title: parent.title,
    }
  })
}

export const SAMPLE_VISUALIZER_AGENTS: VisualizerAgent[] =
  PLACEHOLDER_AGENTS.map((agent) => ({
    key: agent.key,
    label: agent.label,
    lenses: agent.lenses.map((lens) => ({
      ...lens,
      bucketAssignments: resolveFakeLensConversationAssignments(
        agent.key,
        lens.key,
      ).bucketAssignments,
    })),
  }))

function runLensOrder(
  lens:
    | ConversationClusteringRunOutputV1["agents"][number]["lenses"][number]
    | ConversationClusteringRunOutputV2["agents"][number]["lenses"][number],
) {
  return "issues" in lens ? 3 : RUN_LENS_DISPLAY_ORDER[lens.lens_type]
}

/**
 * Maps a backend cluster's (x, y) -- centered at the origin, uniformly scaled so the
 * farthest cluster in the run sits at radius 1 (see the workbench's
 * project_dedup_clusters_2d.py) -- into plate UV space centered at (0.5, 0.5).
 *
 * TODO(clustering-lens-types): drop the cast once `npm run update:backend-types` vendors
 * ConversationClusteringClusterOutputV1.x/y (added alongside
 * samira/clustering-run-personas-anomalies-lenses on the backend); the field is already on
 * the wire from export_clustering_run_output.py --cluster-layout-filename.
 */
function resolveClusterSeedPosition(
  cluster: NonNullable<
    ConversationClusteringRunOutputV1["agents"][number]["lenses"][number]["clusters"]
  >[number],
): VisualizerClusterSeedPosition | undefined {
  const { x, y } = cluster as unknown as {
    x?: number | null
    y?: number | null
  }
  if (x == null || y == null) {
    return undefined
  }
  return {
    x: 0.5 + x * PLATE_DOMAIN_RADIUS,
    y: 0.5 + y * PLATE_DOMAIN_RADIUS,
  }
}

/**
 * Maps a persisted clustering run into visualizer agents. Each cluster becomes
 * one bucket; unclustered conversations are not plotted. Agents whose lenses
 * have no clusters are dropped, so an empty result means nothing to show.
 *
 * `agentsById` resolves each agent's real display name (same lookup/shape
 * `useAgentsById` from `@/agents/catalog` already provides elsewhere, e.g.
 * `RunHeaderMetadata`). The clustering run payload only ever carries
 * `agent_id` -- falls back to the raw id when the org's agent list hasn't
 * loaded yet or no longer has that agent (e.g. deleted since the run).
 */
export function buildVisualizerAgentsFromClusteringRun(
  output: ConversationClusteringRunOutputV1 | ConversationClusteringRunOutputV2,
  agentsById: Record<string, AgentRecord | undefined> = {},
): VisualizerAgent[] {
  return output.agents
    .map((agent) => ({
      key: agent.agent_id,
      label:
        agentsById[agent.agent_id]?.display_name ?? `Agent ${agent.agent_id}`,
      lenses: [...agent.lenses]
        .sort(
          (leftLens, rightLens) =>
            runLensOrder(leftLens) - runLensOrder(rightLens),
        )
        .map((lens): VisualizerLens => {
          if ("issues" in lens) {
            const failureTableIds = resolveFailureTableIds(lens)
            const issuesById = new Map(
              lens.issues.map((issue) => [issue.issue_id, issue]),
            )
            return {
              bucketAssignments: lens.children.map((child) => ({
                bucketKey: child.group_id,
                conversations: child.issue_ids.flatMap((issueId) => {
                  const issue = issuesById.get(issueId)
                  const simulationOutputId = failureTableIds.get(issueId)
                  return issue && simulationOutputId
                    ? [
                        {
                          dotId: issue.issue_id,
                          firstSentence: `${issue.kind}: ${issue.gap}`,
                          id: simulationOutputId,
                          position: {
                            x: 0.5 + issue.x * PLATE_DOMAIN_RADIUS,
                            y: 0.5 + issue.y * PLATE_DOMAIN_RADIUS,
                          },
                        },
                      ]
                    : []
                }),
                label: child.title,
              })),
              buckets: lens.children.map((child) => ({
                count: child.issue_count,
                description: child.description,
                key: child.group_id,
                label: child.title,
              })),
              failureOutput: lens,
              key: "failures",
              preset: DEFAULT_FALLBACK_PRESET,
              subtitle: "Issues found across analyzed conversations",
              title: "Failures",
            }
          }
          const placeholderLens = resolvePlaceholderLens(
            PLACEHOLDER_AGENTS[0].key,
            RUN_LENS_PLACEHOLDER_KEYS[lens.lens_type],
          )
          const clusters = lens.clusters ?? []
          const bucketAssignments = clusters.map((cluster) => ({
            bucketKey: cluster.cluster_id,
            conversations: cluster.simulation_outputs.map(
              (simulationOutput) => ({
                firstSentence: resolveFirstSentence(
                  simulationOutput.description,
                ),
                id: simulationOutput.simulation_output_id,
              }),
            ),
            label: cluster.title,
            seedPosition: resolveClusterSeedPosition(cluster),
          }))
          const buckets = clusters.map((cluster) => ({
            count: cluster.simulation_outputs.length,
            description: cluster.description,
            key: cluster.cluster_id,
            label: cluster.title,
          }))
          const callTopicParents = resolveCallTopicParents(
            lens,
            buckets,
            bucketAssignments,
          )
          return {
            bucketAssignments,
            buckets,
            callTopicParents,
            key: placeholderLens.key,
            preset: placeholderLens.preset,
            subtitle: placeholderLens.subtitle,
            title: placeholderLens.title,
          }
        })
        .filter((lens) => lens.buckets.length > 0 || lens.key === "failures"),
    }))
    .filter((agent) => agent.lenses.length > 0)
}

export function resolveVisualizerAgent(
  agents: VisualizerAgent[],
  agentKey: string | null,
) {
  return agents.find((agent) => agent.key === agentKey) ?? agents[0]
}

function resolveAgentConversationPool(agent: VisualizerAgent) {
  const conversationsById = new Map<string, VisualizerConversation>()
  for (const lens of agent.lenses) {
    for (const bucketAssignment of lens.bucketAssignments) {
      for (const conversation of bucketAssignment.conversations) {
        if (lens.key === "failures") {
          if (!conversationsById.has(conversation.id)) {
            conversationsById.set(conversation.id, {
              firstSentence: "Uploaded conversation",
              id: conversation.id,
            })
          }
        } else {
          conversationsById.set(conversation.id, conversation)
        }
      }
    }
  }
  return Array.from(conversationsById.values())
}

export function buildNoLensVisualizerLens(
  agent: VisualizerAgent,
): VisualizerLens {
  const allConversations = resolveAgentConversationPool(agent)
  const anomalyPreset = agent.lenses.find(
    (lens) => lens.key === "anomalies",
  )?.preset
  const fallbackPreset = agent.lenses[0]?.preset ?? DEFAULT_FALLBACK_PRESET
  const noLensPreset = resolveNoLensPreset(
    agent.key,
    anomalyPreset ?? fallbackPreset,
  )

  return {
    bucketAssignments: [
      {
        bucketKey: ALL_CONVERSATIONS_BUCKET_KEY,
        conversations: allConversations,
        label: NO_LENS_DISPLAY_LABEL,
      },
    ],
    buckets: [
      {
        count: allConversations.length,
        description: NO_LENS_DISPLAY_LABEL,
        key: ALL_CONVERSATIONS_BUCKET_KEY,
        label: NO_LENS_DISPLAY_LABEL,
      },
    ],
    key: NO_LENS_VISUALIZER_LENS_KEY,
    preset: noLensPreset,
    subtitle: NO_LENS_DISPLAY_LABEL,
    title: NO_LENS_DISPLAY_LABEL,
  }
}

export function resolveVisualizerLens(
  agent: VisualizerAgent,
  lensKey: string | null,
) {
  return agent.lenses.find((lens) => lens.key === lensKey) ?? agent.lenses[0]
}
